// ===================================================
// gptClient.js — 프론트엔드 공통 유틸 (축소됨)
//
// 역할:
//   - 인증 토큰 조회 (Edge Function 호출 시 사용)
//   - 대화 내용 텍스트 조합 유틸
//
// 프롬프트 조립, GPT 직접 호출은 Edge Function으로 이전되었습니다.
// 이 파일에는 인증과 유틸 함수만 남아 있습니다.
// ===================================================

import { supabase } from '../supabase';

// 현재 로그인 유저의 인증 토큰 가져오기
// Edge Function에서 유저 신원을 검증할 때 사용합니다.
// 30초 캐싱: 한 흐름에서 반복 호출 방지
const AUTH_TOKEN_TIMEOUT_MS = 5000;
const AUTH_CACHE_TTL_MS = 30_000;
let _authCache = { token: null, expiresAt: 0 };

export async function getAuthToken() {
  if (_authCache.token && Date.now() < _authCache.expiresAt) {
    // 캐시된 토큰이 JWT 만료 직전(5분 이내)이면 갱신 시도
    if (!isTokenExpiringSoon(_authCache.token)) {
      return _authCache.token;
    }
  }
  try {
    // 먼저 현재 세션 확인
    let result = await Promise.race([
      supabase.auth.getSession(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('getAuthToken timeout')), AUTH_TOKEN_TIMEOUT_MS)
      ),
    ]);
    let token = result?.data?.session?.access_token ?? null;

    // 토큰이 만료됐거나 5분 이내 만료 예정이면 갱신 시도
    if (token && isTokenExpiringSoon(token)) {
      try {
        const refreshResult = await supabase.auth.refreshSession();
        token = refreshResult?.data?.session?.access_token ?? token;
      } catch {
        // 갱신 실패 시 기존 토큰 그대로 사용 (서버 RLS가 최종 판단)
      }
    }

    if (token) {
      _authCache = { token, expiresAt: Date.now() + AUTH_CACHE_TTL_MS };
    }
    return token;
  } catch {
    return null;
  }
}

// JWT 토큰이 5분 이내에 만료되는지 확인
function isTokenExpiringSoon(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiresAt = payload.exp * 1000; // ms 변환
    const fiveMinutes = 5 * 60 * 1000;
    return Date.now() > expiresAt - fiveMinutes;
  } catch {
    return false;
  }
}

// 전체 대화 내용을 텍스트로 조합 (공통 유틸)
export function buildContent(initialText, rounds) {
  return [
    `요약: ${initialText}`,
    ...rounds.map((r) =>
      r.questions
        .map((q, qi) => `Q${qi + 1}: ${q}\nA${qi + 1}: ${r.answers[qi] || '(미입력)'}`)
        .join('\n')
    ),
  ].join('\n\n');
}
